import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Search, ShoppingCart, Menu, X, User, ChevronDown } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import Logo from "@/components/common/Logo";
import { useCart } from "@/lib/cart";
import api from "@/lib/api";
import { formatINR } from "@/lib/format";

const NAV = [
  { label: "Reviews", to: "/reviews" },
  { label: "Guides", to: "/guides" },
  { label: "Contact", to: "/contact" },
  { label: "FAQ", to: "/faq" },
];

const ABOUT_LINKS = [
  { label: "Our Journey", to: "/about/our-journey", slug: "our-journey" },
];

const SHOP_CATEGORIES = [
  { label: "Exterior Care", slug: "exterior" },
  { label: "Interior Care", slug: "interior" },
  { label: "Polishes & Wax", slug: "polishes" },
  { label: "Aerosols", slug: "aerosols" },
  { label: "Detailing Accessories", slug: "accessories" },
  { label: "Car Care Kits", slug: "kits" },
];

const BUSINESS_LINKS = [
  { label: "Partner", to: "/partner" },
  { label: "White Label", to: "/white-label" },
];

const ANNOUNCE = "Use code ACC10 and get 10% off";

const POPULAR_SEARCHES = [
  "Car Shampoo", "Scratch", "Rust", "Ceramic Wax",
  "Tyre Shine", "Interior Cleaner", "Microfiber", "AC Cleaner",
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const aboutTimer = useRef(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const { count, setOpen } = useCart();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setAboutOpen(false);
  }, [location.pathname]);

  // Hover-intent for the About dropdown: the label is a real link to /about, while
  // hovering reveals the sub-pages. A short close delay bridges the gap between the
  // trigger and the (portalled) menu content so it doesn't flicker shut.
  const openAbout = () => {
    if (aboutTimer.current) clearTimeout(aboutTimer.current);
    setAboutOpen(true);
  };
  const closeAbout = () => {
    aboutTimer.current = setTimeout(() => setAboutOpen(false), 120);
  };

  const submitSearch = (e) => {
    if (e) e.preventDefault();
    if (searchTerm.trim()) runSearch(searchTerm.trim());
  };

  const closeSearch = () => {
    setSearchOpen(false);
    setSearchTerm("");
    setSuggestions([]);
  };

  const runSearch = (term) => {
    navigate(`/shop?search=${encodeURIComponent(term)}`);
    closeSearch();
  };

  const goToProduct = (slug) => {
    navigate(`/product/${slug}`);
    closeSearch();
  };

  // Debounced autosuggest
  useEffect(() => {
    const q = searchTerm.trim();
    if (q.length < 2) { setSuggestions([]); return undefined; }
    const t = setTimeout(() => {
      api.getProducts({ search: q, limit: 6 })
        .then((d) => setSuggestions(d.items || []))
        .catch(() => setSuggestions([]));
    }, 220);
    return () => clearTimeout(t);
  }, [searchTerm]);

  return (
    <header className="sticky top-0 z-50 w-full">
      {/* Announcement marquee */}
      <div className="acx-marquee-track h-8 overflow-hidden border-b border-[color:var(--acx-border)] bg-black" data-testid="announcement-marquee">
        <div className="acx-marquee h-full items-center">
          {Array.from({ length: 12 }).map((_, i) => (
            <span key={i} className="flex items-center text-[12px] tracking-[0.08em] text-white/70">
              <span className="mx-6">{ANNOUNCE}</span>
              <span className="text-[#E11B22]">●</span>
            </span>
          ))}
        </div>
      </div>

      {/* Main bar */}
      <div
        className={`border-b border-[color:var(--acx-border)] transition-colors duration-300 ${
          scrolled ? "bg-black/80 backdrop-blur-xl" : "bg-black/40 backdrop-blur-md"
        }`}
      >
        <div className="acx-container flex h-16 items-center justify-between px-4 sm:px-6 lg:px-10">
          <Logo />

          <nav className="hidden items-center gap-7 lg:flex">
            {/* Shop dropdown exposing categories */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="nav-shop"
                  className={`inline-flex items-center gap-1 text-[14px] font-medium outline-none transition-colors hover:text-white ${
                    location.pathname.startsWith("/shop") ? "text-white" : "text-white/65"
                  }`}
                >
                  Shop <ChevronDown size={14} className="opacity-70" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56 border-[color:var(--acx-border)] bg-[var(--acx-card)] text-white">
                <DropdownMenuItem onClick={() => navigate("/shop")} data-testid="nav-shop-all" className="cursor-pointer font-semibold focus:bg-white/10 focus:text-white">
                  All Products
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-[color:var(--acx-border)]" />
                {SHOP_CATEGORIES.map((c) => (
                  <DropdownMenuItem
                    key={c.slug}
                    onClick={() => navigate(`/shop?category=${c.slug}`)}
                    data-testid={`nav-category-${c.slug}`}
                    className="cursor-pointer text-white/80 focus:bg-white/10 focus:text-white"
                  >
                    {c.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* About dropdown — the label links to /about; hovering reveals the
                two About sub-pages. Matches Shop / For Business dropdown styling. */}
            <div className="relative" onMouseEnter={openAbout} onMouseLeave={closeAbout}>
              <DropdownMenu open={aboutOpen} onOpenChange={setAboutOpen}>
                <DropdownMenuTrigger asChild>
                  <Link
                    to="/about"
                    data-testid="nav-about"
                    className={`inline-flex items-center gap-1 text-[14px] font-medium outline-none transition-colors hover:text-white ${
                      location.pathname.startsWith("/about") ? "text-white" : "text-white/65"
                    }`}
                  >
                    About <ChevronDown size={14} className="opacity-70" />
                  </Link>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="start"
                  onMouseEnter={openAbout}
                  onMouseLeave={closeAbout}
                  className="w-56 border-[color:var(--acx-border)] bg-[var(--acx-card)] text-white"
                >
                  {ABOUT_LINKS.map((a) => (
                    <DropdownMenuItem
                      key={a.to}
                      onClick={() => navigate(a.to)}
                      data-testid={`nav-about-${a.slug}`}
                      className="cursor-pointer text-white/80 focus:bg-white/10 focus:text-white"
                    >
                      {a.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {NAV.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`text-[14px] font-medium transition-colors hover:text-white ${
                  location.pathname === item.to ? "text-white" : "text-white/65"
                }`}
              >
                {item.label}
              </Link>
            ))}

            {/* For Business dropdown (groups Partner + White Label) */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="nav-for-business"
                  className={`inline-flex items-center gap-1 text-[14px] font-medium outline-none transition-colors hover:text-white ${
                    ["/partner", "/white-label"].includes(location.pathname) ? "text-white" : "text-white/65"
                  }`}
                >
                  For Business <ChevronDown size={14} className="opacity-70" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-44 border-[color:var(--acx-border)] bg-[var(--acx-card)] text-white">
                {BUSINESS_LINKS.map((b) => (
                  <DropdownMenuItem
                    key={b.to}
                    onClick={() => navigate(b.to)}
                    data-testid={`nav-business-${b.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="cursor-pointer text-white/80 focus:bg-white/10 focus:text-white"
                  >
                    {b.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>

          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setSearchOpen(true)}
              data-testid="header-search-button"
              className="inline-flex h-10 w-10 items-center justify-center rounded-full text-white/80 transition-colors hover:bg-white/10"
              aria-label="Search"
            >
              <Search size={19} />
            </button>

            <Link
              to="/account"
              data-testid="header-account-button"
              className="inline-flex h-10 w-10 items-center justify-center rounded-full text-white/80 transition-colors hover:bg-white/10"
              aria-label="My account"
              title="My account & orders"
            >
              <User size={19} />
            </Link>

            <button
              onClick={() => setOpen(true)}
              data-testid="header-cart-button"
              className="relative inline-flex h-10 w-10 items-center justify-center rounded-full text-white/80 transition-colors hover:bg-white/10"
              aria-label="Cart"
            >
              <ShoppingCart size={19} />
              {count > 0 && (
                <span className="absolute -right-0.5 -top-0.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-[#E11B22] px-1 text-[10px] font-bold text-white">
                  {count}
                </span>
              )}
            </button>
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <button
                  data-testid="header-menu-button"
                  className="inline-flex h-10 w-10 items-center justify-center rounded-full text-white/80 transition-colors hover:bg-white/10 lg:hidden"
                  aria-label="Menu"
                >
                  <Menu size={20} />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] border-l border-[color:var(--acx-border)] bg-[var(--acx-bg-2)] p-0 text-white">
                <div className="flex items-center justify-between border-b border-[color:var(--acx-border)] p-5">
                  <Logo size="sm" showTagline={false} />
                  <button onClick={() => setMobileOpen(false)} aria-label="Close"><X size={20} className="text-white/70" /></button>
                </div>
                <nav className="flex flex-col p-2">
                  <Link
                    to="/shop"
                    data-testid="mobile-nav-shop"
                    className="rounded-lg px-4 py-3.5 text-[15px] font-semibold text-white transition-colors hover:bg-white/5"
                  >
                    Shop All
                  </Link>
                  <div className="mb-1 grid grid-cols-2 gap-1 px-2 pb-2">
                    {SHOP_CATEGORIES.map((c) => (
                      <Link
                        key={c.slug}
                        to={`/shop?category=${c.slug}`}
                        data-testid={`mobile-nav-category-${c.slug}`}
                        className="rounded-lg px-2.5 py-2 text-[13px] font-medium text-white/65 transition-colors hover:bg-white/5 hover:text-white"
                      >
                        {c.label}
                      </Link>
                    ))}
                  </div>
                  <div className="my-1 h-px bg-[color:var(--acx-border)]" />
                  {/* About + sub-pages (expanded list, consistent with Shop/For Business on mobile) */}
                  <Link
                    to="/about"
                    data-testid="mobile-nav-about"
                    className="rounded-lg px-4 py-3.5 text-[15px] font-semibold text-white transition-colors hover:bg-white/5"
                  >
                    About
                  </Link>
                  <div className="mb-1 flex flex-col px-2 pb-2">
                    {ABOUT_LINKS.map((a) => (
                      <Link
                        key={a.to}
                        to={a.to}
                        data-testid={`mobile-nav-about-${a.slug}`}
                        className="rounded-lg px-2.5 py-2 text-[13px] font-medium text-white/65 transition-colors hover:bg-white/5 hover:text-white"
                      >
                        {a.label}
                      </Link>
                    ))}
                  </div>
                  {NAV.map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className="rounded-lg px-4 py-3.5 text-[15px] font-medium text-white/80 transition-colors hover:bg-white/5 hover:text-white"
                    >
                      {item.label}
                    </Link>
                  ))}
                  <p className="px-4 pb-1 pt-3 text-[11px] uppercase tracking-[0.18em] text-white/40">For Business</p>
                  {BUSINESS_LINKS.map((b) => (
                    <Link
                      key={b.to}
                      to={b.to}
                      data-testid={`mobile-nav-business-${b.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className="rounded-lg px-4 py-3.5 text-[15px] font-medium text-white/80 transition-colors hover:bg-white/5 hover:text-white"
                    >
                      {b.label}
                    </Link>
                  ))}
                  <div className="my-2 h-px bg-[color:var(--acx-border)]" />
                  <Link to="/account" className="flex items-center gap-2.5 rounded-lg px-4 py-3.5 text-[15px] font-medium text-white/80 transition-colors hover:bg-white/5 hover:text-white">
                    <User size={17} /> My Account &amp; Orders
                  </Link>
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>

      <Dialog open={searchOpen} onOpenChange={(o) => (o ? setSearchOpen(true) : closeSearch())}>
        <DialogContent className="top-[10%] z-[100] max-w-xl translate-y-0 gap-0 overflow-hidden border-[color:var(--acx-border)] bg-[var(--acx-card)] p-0 text-white">
          <form onSubmit={submitSearch} className="flex items-center gap-3 border-b border-[color:var(--acx-border)] px-4 py-3.5">
            <Search size={20} className="shrink-0 text-white/50" />
            <input
              autoFocus
              data-testid="header-search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search for products, problems (e.g. scratch, rust)..."
              className="flex-1 bg-transparent text-[15px] text-white placeholder:text-white/40 focus:outline-none"
            />
            {searchTerm && (
              <button type="button" onClick={() => setSearchTerm("")} aria-label="Clear" className="text-white/50 hover:text-white">
                <X size={17} />
              </button>
            )}
          </form>

          <div className="max-h-[60vh] overflow-y-auto p-4" data-testid="search-panel-body">
            {searchTerm.trim().length >= 2 ? (
              suggestions.length > 0 ? (
                <div className="space-y-1">
                  <p className="px-1 pb-1 text-[11px] uppercase tracking-[0.16em] text-white/40">Products</p>
                  {suggestions.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => goToProduct(p.slug)}
                      data-testid={`search-suggestion-${p.slug}`}
                      className="flex w-full items-center gap-3 rounded-[10px] p-2 text-left transition-colors hover:bg-white/5"
                    >
                      <img src={p.images?.[0]} alt="" className="h-11 w-11 shrink-0 rounded-[8px] object-cover" />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[14px] font-medium text-white">{p.name}</span>
                        <span className="block text-[12px] capitalize text-white/45">{p.category}</span>
                      </span>
                      <span className="shrink-0 text-[14px] font-semibold text-white">{formatINR(p.price)}</span>
                    </button>
                  ))}
                  <button
                    onClick={() => submitSearch()}
                    className="mt-1 flex w-full items-center justify-center rounded-[10px] bg-white/5 px-4 py-2.5 text-[13px] font-semibold text-white transition-colors hover:bg-white/10"
                  >
                    See all results for “{searchTerm.trim()}”
                  </button>
                </div>
              ) : (
                <div className="py-6 text-center" data-testid="search-no-results">
                  <p className="text-[14px] text-white/70">No matches for “{searchTerm.trim()}”.</p>
                  <p className="mt-1 text-[13px] text-white/45">Try one of these popular searches:</p>
                  <div className="mt-4 flex flex-wrap justify-center gap-2">
                    {POPULAR_SEARCHES.map((term) => (
                      <button
                        key={term}
                        onClick={() => runSearch(term)}
                        className="rounded-full border border-[color:var(--acx-border)] bg-white/5 px-3 py-1.5 text-[13px] text-white/75 transition-colors hover:bg-white/10 hover:text-white"
                      >
                        {term}
                      </button>
                    ))}
                  </div>
                </div>
              )
            ) : (
              <div className="space-y-5">
                <div>
                  <p className="mb-2.5 text-[11px] uppercase tracking-[0.16em] text-white/40">Popular searches</p>
                  <div className="flex flex-wrap gap-2">
                    {POPULAR_SEARCHES.map((term) => (
                      <button
                        key={term}
                        onClick={() => runSearch(term)}
                        data-testid={`search-popular-${term.toLowerCase().replace(/\s+/g, "-")}`}
                        className="rounded-full border border-[color:var(--acx-border)] bg-white/5 px-3 py-1.5 text-[13px] text-white/75 transition-colors hover:bg-white/10 hover:text-white"
                      >
                        {term}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="mb-2.5 text-[11px] uppercase tracking-[0.16em] text-white/40">Shop by category</p>
                  <div className="grid grid-cols-2 gap-1.5">
                    {SHOP_CATEGORIES.map((c) => (
                      <button
                        key={c.slug}
                        onClick={() => { navigate(`/shop?category=${c.slug}`); closeSearch(); }}
                        data-testid={`search-category-${c.slug}`}
                        className="flex items-center gap-2 rounded-[10px] px-3 py-2.5 text-left text-[14px] text-white/75 transition-colors hover:bg-white/5 hover:text-white"
                      >
                        <ChevronDown size={14} className="-rotate-90 text-white/30" /> {c.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </header>
  );
};

export default Header;
